import { useEffect, useState } from 'react'
import parse from 'html-react-parser'
import Image from 'next/image'
import Link from 'next/link'

import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button-v1'
import { useToast } from '@wartek-id/toast-v2'
import { Sheet, SheetContent } from '@wartek-id/sheet'

import Header from 'app/BuktiKaryaPage/components/Header'
import { DocUploader } from '../blocks/DocUploader'
import AndroidHandler from 'utils/androidHandler'
import {
  useMutateAPIDeleteKSInvitationDocuments,
  useMutateAPIFinalizeSubmission,
  useMutateAPIUploadSubmission,
  useQueryAPIGetKSInvitationDocuments,
  useQueryAPIGetKSSelectionCandidate,
} from 'api/bcks/useAPIBCKSInvitation'

const createFormData = (object: any): FormData => {
  const formData = new FormData()
  Object.keys(object).forEach((key) => formData.append(key, object[key]))

  return formData
}

export const BCKSUploadNapzaPage = () => {
  const androidHandler = AndroidHandler()
  const toast = useToast()

  const { data: candidate } = useQueryAPIGetKSSelectionCandidate()
  const { data: documents, refetch } = useQueryAPIGetKSInvitationDocuments(
    candidate?.selectionId || ''
  )
  const { mutateAsync: uploadSubmission, isLoading: isUploading } =
    useMutateAPIUploadSubmission(candidate?.selectionId || '')
  const { mutateAsync: finalize } = useMutateAPIFinalizeSubmission(
    candidate?.selectionId || ''
  )
  const { mutateAsync: deleteFile } = useMutateAPIDeleteKSInvitationDocuments()

  const [file, setFile] = useState('')
  const [isUpload, setIsUpload] = useState(false)
  const [sheet, setSheet] = useState('')

  useEffect(() => {
    if (documents) {
      setFile(documents?.napzaUrl)
      setIsUpload(false)
    }
  }, [candidate?.selectionId, documents])

  const handleSelectDoc = async (_, data) => {
    try {
      await uploadSubmission(
        createFormData({
          selectionId: candidate?.selectionId,
          fileType: 'napza',
          file: data.fileData,
        })
      )

      refetch()
    } catch (error) {
      toast({
        message: 'Gagal mengunggah dokumen',
      })
    }
  }

  const handleDeleteFile = async () => {
    try {
      await deleteFile({
        selectionId: candidate?.selectionId,
        fileType: 'NAPZA',
      })

      setFile(null)
    } catch (error) {
      toast({
        message: 'Gagal menghapus dokumen',
      })
    }
  }

  const handleCopyUrlClick = (urlToClipboard: string) => {
    if (androidHandler?.enabled) {
      androidHandler?.copyText(urlToClipboard)
      toast({
        message: 'Tautan berhasil disalin',
      })
      return
    }

    if (!navigator?.clipboard) {
      toast({
        message: 'Tautan gagal disalin',
      })
    }
    navigator.clipboard.writeText(urlToClipboard).then(
      function () {
        toast({
          message: 'Tautan berhasil disalin',
        })
      },
      function (err) {
        toast({
          message: `Tautan gagal disalin: ${err}`,
        })
      }
    )
  }

  const handleUpload = async () => {
    try {
      await finalize({})

      setSheet('success')
    } catch (error) {
      toast({
        message: 'Gagal mengubah status',
      })
    }
  }

  if (isUpload) {
    return (
      <DocUploader
        selectedDocMeta={{ title: 'Dokumen Napza' }}
        docData={{ url: file }}
        onDocUpload={handleSelectDoc}
        onBackClick={() => setIsUpload(false)}
        isReviewMode
      />
    )
  }

  return (
    <>
      <div className="relative flex flex-col h-screen max-w-xl mx-auto">
        <Header title="Pengumpulan Dokumen" forceNavigateBack />

        <div className="bg-surface-selected-default p-4 mt-3">
          <Text variant="body-sm">
            Unggah Hasil Pindai Dokumen NAPZA Asli, ukuran{' '}
            <strong>maks. 5 MB</strong>.
          </Text>
        </div>

        <div className="p-4">
          <section className="flex mb-6 p-3 border border-subdued rounded-lg">
            <div className="w-fit mr-4">
              <Image
                layout="fixed"
                src="/images/img-typing-monitor.png"
                width={84}
                height={84}
                alt="Ilustrasi pengunggahan berkas"
              />
            </div>
            <div>
              <Text variant="body-sm" color="subdued">
                {parse(
                  `Pengunggahan dokumen dianjurkan melalui platform web <b>Ruang GTK</b>`
                )}
              </Text>

              <Button
                className="mt-1.5"
                color="black"
                size="sm"
                onClick={() => {
                  handleCopyUrlClick(window.location.origin)
                }}
              >
                Salin tautan
              </Button>
            </div>
          </section>

          <section className="flex flex-col gap-1">
            <Text variant="body">Dokumen Napza</Text>
            <Text variant="helper" color="subdued">
              Unggah dokumen NAPZA terbaru.
            </Text>
            {file ? (
              <div className="rounded w-full mt-2 p-3 border border-black">
                <div className="flex flex-row items-center justify-between">
                  <div className="flex flex-row items-center">
                    <Icon>text_snippet</Icon>
                    <Text className="ml-2" variant="body">
                      Dokumen Napza.pdf
                    </Text>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    color="red"
                    onClick={() => handleDeleteFile()}
                  >
                    Hapus
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="cursor-pointer rounded w-full border-dashed p-3 border-2 border-subdued"
                onClick={() => setIsUpload(true)}
              >
                <div className="flex flex-row items-center">
                  <Icon className="-rotate-45">attachment</Icon>
                  <Text color="subdued" className="ml-2 italic">
                    Pilih Dokumen
                  </Text>
                </div>
              </div>
            )}
          </section>

          <div className="flex gap-4 mt-6">
            <Link href="/bcks/invitation/final" passHref>
              <Button
                fullWidth
                size="lg"
                color="white"
                disabled={!file || Boolean(sheet)}
              >
                Simpan Draf
              </Button>
            </Link>
            <Button
              fullWidth
              size="lg"
              disabled={!file || Boolean(sheet)}
              onClick={() => setSheet('confirm')}
            >
              Lanjutkan
            </Button>
          </div>
        </div>
      </div>

      <Sheet isOpen={sheet === 'confirm'} onClose={() => setSheet('')}>
        <SheetContent className="max-w-xl m-auto p-4 pb-6">
          <Text variant="heading-sm">Unggah dokumen?</Text>
          <Text variant="body-sm" color="subdued" className="mt-1.5 mb-6">
            Pastikan semua berkas yang Anda kumpulkan benar dan memenuhi syarat.
            Setelah dikumpulkan, berkas tidak dapat dihapus/diubah.
          </Text>
          <div className="flex flex-col gap-2.5 p-1">
            <Button
              onClick={() => handleUpload()}
              disabled={isUploading}
              isLoading={isUploading}
            >
              Ya, unggah sekarang
            </Button>
            <Button color="white" onClick={() => setSheet('')}>
              Cek ulang berkas
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet isOpen={sheet === 'success'} onClose={() => setSheet('')}>
        <SheetContent className="max-w-xl m-auto p-4 pb-6">
          <div className="flex items-start gap-4 mb-4">
            <Text variant="heading-sm">
              Dokumen berhasil dikumpulkan dan akan dicek oleh Dinas!
            </Text>
            <Image
              src={'/images/bcks/img-folder-file-document-download.png'}
              alt={'image-placeholder'}
              width={72}
              height={72}
            />
          </div>
          <Link href="/bcks/invitation/final" passHref>
            <Button color="black">Saya mengerti</Button>
          </Link>
        </SheetContent>
      </Sheet>
    </>
  )
}
